// src/pages/MyRides.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { isToday, parseISO, format } from "date-fns";
import socket from "../socket";
import RideBottomNav from "../components/RideBottomNav";

const API_WRK = process.env.REACT_APP_API_WRK;
const API_BASE = API_WRK?.replace("/api/worker", "");
const API_RIDE = `${API_BASE}/api/ride`;
const API_RIDEPAY = `${API_BASE}/api/ridepayment`;

const MyRides = () => {
  const [rides, setRides] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [tab, setTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const workerId = localStorage.getItem("workerId");
  const navigate = useNavigate();

  /* =====================================================
     FETCH RIDES
  ===================================================== */
  useEffect(() => {
    const fetchRides = async () => {
      try {
        const res = await axios.get(`${API_RIDE}/my-rides/${workerId}`);
        setRides(res.data || []);
      } catch (err) {
        console.error("❌ Failed to load rides:", err);
      } finally {
        setLoading(false);
      }
    };
    if (workerId) fetchRides();
  }, [workerId]);

  /* =====================================================
     SOCKET LISTENERS
  ===================================================== */
  useEffect(() => {
    if (!workerId) return;

    socket.emit("join-worker", workerId);

    socket.on("ride-status-update", (data) => {
      setRides((prev) =>
        prev.map((r) =>
          String(r._id) === String(data.rideId)
            ? { ...r, status: data.status || r.status, updatedAt: new Date() }
            : r
        )
      );
    });

    socket.on("ride-message", (data) => {
      if (!data?.rideId) return;
      setRides((prev) =>
        prev.map((r) =>
          String(r._id) === String(data.rideId)
            ? { ...r, unreadChats: (r.unreadChats || 0) + 1 }
            : r
        )
      );
    });

    socket.on("ride-new-request", (newReq) => {
      if (!newReq?.rideId) return;
      const rideIdFromEvent = newReq.rideId?._id || newReq.rideId;
      setRides((prev) =>
        prev.map((r) =>
          String(r._id) === String(rideIdFromEvent)
            ? { ...r, unreadRequests: (r.unreadRequests || 0) + 1 }
            : r
        )
      );
    });

    return () => {
      socket.off("ride-status-update");
      socket.off("ride-message");
      socket.off("ride-new-request");
    };
  }, [workerId]);

  /* =====================================================
     FILTER LOGIC
  ===================================================== */
  useEffect(() => {
    const normalize = (d) => {
      if (!d) return null;
      try {
        return (typeof d === "string" ? parseISO(d) : new Date(d)).setHours(
          0,
          0,
          0,
          0
        );
      } catch {
        return null;
      }
    };

    const todayStamp = new Date().setHours(0, 0, 0, 0);

    const filters = {
      // ➕ ACTIVE = live rides + awaiting customer confirmation
      active: (r) => {
        const stamp = normalize(r.date);
        return (
          !r.isArchived &&
          ["active", "pending", "accepted", "full", "worker_completed"].includes(
            r.status
          ) &&
          stamp !== null &&
          stamp >= todayStamp
        );
      },

      // 🕒 PAST = any finished / cancelled / disputed ride in the past
      past: (r) => {
        const stamp = normalize(r.date);
        return (
          [
            "completed",
            "worker_completed",
            "customer_completed",
            "fully_completed",
            "cancelled",
            "cancelled_by_driver",
            "disputed",
            "refunded",
          ].includes(r.status) &&
          stamp !== null &&
          stamp < todayStamp
        );
      },

      // ✅ COMPLETED TAB = truly done rides
      completed: (r) =>
        ["completed", "customer_completed", "fully_completed", "refunded"].includes(
          r.status
        ),

      // ❌ CANCELLED TAB
      cancelled: (r) =>
        ["cancelled", "cancelled_by_driver", "disputed"].includes(r.status),
    };

    const filterFn = filters[tab] || (() => true);
    setFiltered(rides.filter(filterFn));
  }, [tab, rides]);

  /* =====================================================
     CANCEL RIDE
  ===================================================== */
  const handleCancel = async (rideId) => {
    if (!window.confirm("Cancel this ride?")) return;
    try {
      await axios.post(`${API_BASE}/api/ridepayment/cancel-by-worker`, {
        rideId,
        workerId,
      });
      socket.emit("ride-status-update", { rideId, status: "cancelled" });
      alert("Ride cancelled.");
    } catch {
      alert("Failed to cancel ride.");
    }
  };

  /* =====================================================
     COMPLETE RIDE (whole ride – not used much now)
  ===================================================== */
  const handleComplete = async (rideId) => {
    if (!window.confirm("Mark ride complete?")) return;
    try {
      await axios.post(`${API_RIDEPAY}/worker-complete`, { rideId, workerId });
      socket.emit("ride-status-update", {
        rideId,
        status: "worker_completed",
      });
      alert("Ride marked complete.");
    } catch {
      alert("Failed to complete ride.");
    }
  };

  /* =====================================================
     OPEN REQUESTS PAGE
  ===================================================== */
  const handleOpenRequests = (rideId) => {
    setRides((prev) =>
      prev.map((r) =>
        String(r._id) === String(rideId)
          ? { ...r, unreadRequests: 0, unreadChats: 0 }
          : r
      )
    );
    navigate(`/ride/requests/${rideId}`);
  };

  const tabStyle = (name) =>
    `px-4 py-2 rounded-full font-medium text-sm ${
      tab === name
        ? "bg-blue-600 text-white"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`;

  if (loading) return <div className="p-6">Loading your rides...</div>;

  /* =====================================================
     UI
  ===================================================== */
  return (
    <>
      <div className="space-y-6 font-[Poppins] p-6 pb-24">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          Your Rides
        </h2>

        {/* TABS */}
        <div className="flex flex-wrap gap-3">
          {["active", "past", "completed", "cancelled"].map((name) => (
            <button
              key={name}
              onClick={() => setTab(name)}
              className={tabStyle(name)}
            >
              {name.charAt(0).toUpperCase() + name.slice(1)}
            </button>
          ))}
        </div>

        {/* RIDE CARDS */}
        {filtered.length === 0 ? (
          <p className="text-gray-600 mt-6 text-center">No rides found.</p>
        ) : (
          filtered.map((ride) => {
            let rideDate = null;

            try {
              rideDate = ride?.date ? parseISO(ride.date) : null;
            } catch {}

            const unreadTotal =
              (ride.unreadRequests || 0) + (ride.unreadChats || 0);

            // Seats info
            const booked =
              typeof ride.seatsBooked === "number" ? ride.seatsBooked : 0;
            const totalSeats =
              typeof ride.seatsAvailable === "number"
                ? ride.seatsAvailable
                : null;

            /* FULL STATUS */
            let finalStatus = ride.status || "active";
            if (
              typeof booked === "number" &&
              typeof totalSeats === "number" &&
              booked >= totalSeats &&
              ["active", "pending", "accepted"].includes(finalStatus)
            ) {
              finalStatus = "full";
            }

            return (
              <div
                key={ride._id}
                className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 shadow p-6 rounded-3xl border border-indigo-100 hover:shadow-lg hover:scale-[1.01] transition"
              >
                {/* HEADER */}
                <div className="flex justify-between items-center mt-3">
                  <div className="text-lg font-semibold text-blue-800">
                    {ride.from} → {ride.to}
                  </div>

                  {/* STATUS BADGE */}
                  <span
                    className={`text-sm px-3 py-1 rounded-full font-semibold capitalize ${
                      finalStatus === "full"
                        ? "bg-purple-100 text-purple-700"
                        : finalStatus === "worker_completed"
                        ? "bg-yellow-100 text-yellow-700"
                        : finalStatus === "completed" ||
                          finalStatus === "fully_completed"
                        ? "bg-green-100 text-green-700"
                        : finalStatus === "customer_completed"
                        ? "bg-blue-100 text-blue-700"
                        : finalStatus === "refunded"
                        ? "bg-purple-100 text-purple-700"
                        : finalStatus === "cancelled" ||
                          finalStatus === "cancelled_by_driver"
                        ? "bg-red-100 text-red-700"
                        : finalStatus === "disputed"
                        ? "bg-orange-100 text-orange-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {finalStatus === "worker_completed"
                      ? "Awaiting Customer"
                      : finalStatus
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                </div>

                {/* DETAILS */}
                <div className="text-sm text-gray-700 mt-1 space-y-1">
                  <div>
                    <strong>Date:</strong>{" "}
                    {rideDate ? format(rideDate, "MMMM d, yyyy") : "TBD"} •{" "}
                    <strong>Time:</strong> {ride.time || "TBD"}
                  </div>
                  <div>
                    <strong>Seats:</strong>{" "}
                    {totalSeats !== null ? totalSeats : "—"} •{" "}
                    <strong>Accepted Riders:</strong>{" "}
                    {totalSeats !== null ? `${booked}/${totalSeats}` : booked}
                  </div>
                  <div>
                    <strong>Price/Seat:</strong>{" "}
                    {typeof ride.pricePerSeat === "number"
                      ? `$${ride.pricePerSeat.toFixed(2)}`
                      : ride.pricePerSeat || "—"}
                  </div>
                </div>

                {/* BUTTONS */}
                <div className="flex flex-wrap gap-3 mt-4">
                  {["active", "pending", "accepted", "full", "worker_completed"].includes(
                    finalStatus
                  ) && (
                    <>
                      <button
                        onClick={() => handleCancel(ride._id)}
                        className="px-4 py-2 border text-sm rounded-md bg-red-50 text-red-600 hover:bg-red-100"
                      >
                        Cancel
                      </button>

                      <button
                        onClick={() => navigate(`/ride/edit/${ride._id}`)}
                        className="px-4 py-2 border text-sm rounded-md bg-yellow-50 text-yellow-700 hover:bg-yellow-100"
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => navigate(`/ride/riders/${ride._id}`)}
                        className="px-4 py-2 border text-sm rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                      >
                        Your Riders
                      </button>

                      <button
                        onClick={() => handleOpenRequests(ride._id)}
                        className="relative px-4 py-2 border text-sm rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100"
                      >
                        Requests
                        {unreadTotal > 0 && (
                          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                            {unreadTotal}
                          </span>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <RideBottomNav />
    </>
  );
};

export default MyRides;
